import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronUp, Shield, User } from "lucide-react";

import { useAuth } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

/**
 * PartnerLoginTrigger — footer-friendly, low-emphasis entry point for the
 * Admin (Google OAuth) + Staff (email/password) portals.
 *
 * This component is intentionally visually de-emphasised: small text,
 * muted slate color, no background chrome — matching the visual weight
 * of "Terms & Conditions" and other utility links customers don't need
 * to see prominently. Previously the same functionality lived as a
 * "Partner Login" dropdown in the primary top navigation, which
 * exposed the existence and auth mechanism (Google OAuth) of the
 * staff/admin portal to every first-time customer visitor. Moving it
 * to the footer keeps the same destination but removes the marketing
 * noise for customers.
 *
 * The dropdown is opened by clicking the trigger button (touch- and
 * keyboard-friendly). It closes on: another trigger click, a click on
 * one of the menu items, a click anywhere outside the dropdown, or the
 * Escape key. The Staff login modal is owned by this component so it's
 * self-contained and can be reused anywhere.
 */
export const PartnerLoginTrigger = () => {
  const [open, setOpen] = useState(false);
  const [showStaffLogin, setShowStaffLogin] = useState(false);
  const [staffEmail, setStaffEmail] = useState("");
  const [staffPassword, setStaffPassword] = useState("");
  const [staffLoading, setStaffLoading] = useState(false);
  const [showStaffPassword, setShowStaffPassword] = useState(false);
  // Container ref used by the outside-click listener to distinguish
  // clicks that occurred inside the dropdown (which either close it via
  // their own onClick or are meant to leave it open, e.g. focussing an
  // internal element) from clicks anywhere else on the page.
  const containerRef = useRef(null);

  const { login, staffLogin } = useAuth();
  const navigate = useNavigate();

  // Close the menu on: (a) any click outside `containerRef` and
  // (b) the Escape key. Listeners are attached only while the menu is
  // open so they don't fight with other document-level handlers or
  // leak on unmount. Using `mousedown` (not `click`) fires before any
  // downstream click handler races the setState, which prevents the
  // menu-item onClick from being cancelled by an early close.
  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("touchstart", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("touchstart", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const handleStaffLogin = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const email = staffEmail.trim().toLowerCase();
    const password = staffPassword;
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }
    setStaffLoading(true);
    try {
      const result = await staffLogin(email, password);
      setStaffLoading(false);
      if (result.success) {
        setShowStaffLogin(false);
        setStaffEmail("");
        setStaffPassword("");
        setTimeout(() => navigate("/dashboard"), 100);
      } else {
        toast.error(result.error || "Login failed");
      }
    } catch (err) {
      setStaffLoading(false);
      console.error("[StaffLogin] Unexpected error:", err);
      toast.error("An unexpected error occurred");
    }
  };

  return (
    <>
      <div className="relative" ref={containerRef}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-slate-500 hover:text-slate-300 text-xs transition-colors inline-flex items-center gap-1 font-inter"
          data-testid="footer-partner-login-btn"
          aria-haspopup="menu"
          aria-expanded={open}
        >
          Partner Login
          <ChevronUp
            className={`w-3 h-3 transition-transform ${
              open ? "rotate-0" : "rotate-180"
            }`}
          />
        </button>
        {open && (
          <div
            role="menu"
            className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-52 py-2 glass rounded-lg z-50 shadow-lg"
          >
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                login();
              }}
              className="w-full px-4 py-2 text-left text-slate-300 hover:text-saturn-orange hover:bg-slate-800/50 font-rajdhani flex items-center gap-2 text-sm"
              data-testid="footer-admin-login-btn"
            >
              <Shield className="w-4 h-4" />
              Admin
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setShowStaffLogin(true);
              }}
              className="w-full px-4 py-2 text-left text-slate-300 hover:text-saturn-orange hover:bg-slate-800/50 font-rajdhani flex items-center gap-2 text-sm"
              data-testid="footer-staff-login-btn"
            >
              <User className="w-4 h-4" />
              Staff Login
            </button>
          </div>
        )}
      </div>

      {/* Staff Login Modal */}
      <Dialog open={showStaffLogin} onOpenChange={setShowStaffLogin}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-[90vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-saturn-orange" />
              Staff Login
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleStaffLogin} className="space-y-4 pt-4">
            <div>
              <Label htmlFor="staff-email" className="text-slate-400">
                Email
              </Label>
              <Input
                id="staff-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                value={staffEmail}
                onChange={(e) => setStaffEmail(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white text-base"
                placeholder="staff@example.com"
                data-testid="staff-email-input"
              />
            </div>
            <div>
              <Label htmlFor="staff-password" className="text-slate-400">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="staff-password"
                  type={showStaffPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={staffPassword}
                  onChange={(e) => setStaffPassword(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white text-base pr-16"
                  placeholder="Enter password"
                  data-testid="staff-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowStaffPassword(!showStaffPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white text-xs px-2 py-1"
                >
                  {showStaffPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowStaffLogin(false)}
                className="border-slate-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={staffLoading}
                className="bg-saturn-orange hover:bg-saturn-orange/90"
                data-testid="staff-login-btn"
              >
                {staffLoading ? "Logging in..." : "Login"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PartnerLoginTrigger;
